import { useCallback, useEffect, useState } from 'react'
import { useOrgs } from '../lib/orgs.js'
import { useApi } from '../lib/useApi.js'
import type { DeviceCodeRequest } from '../lib/api.js'
import { oauthScopeWords } from '@nimblybase/contracts'

/**
 * Where a person approves a device — V1-16, RFC 8628 §3.3.
 *
 * Reached two ways: a link the device rendered, with the code already
 * in the URL, or by typing eight characters read off another screen.
 * Both end at the same confirmation, because approving without seeing
 * what is being approved is not approval.
 */
export default function DeviceApprove() {
  const api = useApi()
  const { active } = useOrgs()
  const fromUrl = new URLSearchParams(window.location.search).get('user_code') ?? ''

  const [code, setCode] = useState(fromUrl)
  const [request, setRequest] = useState<DeviceCodeRequest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'approved' | 'denied' | null>(null)

  const look = useCallback(async (value: string) => {
    setBusy(true)
    setError(null)
    try {
      setRequest(await api.describeDeviceCode(value))
    } catch {
      // The API gives one answer for malformed, unknown, expired and
      // already-decided, so that anybody guessing learns nothing. The
      // wording here has to cover all four honestly.
      setError('That code is not valid any more. Codes last fifteen minutes and can be used once — ask the application for a new one.')
      setRequest(null)
    }
    setBusy(false)
  }, [api])

  useEffect(() => { if (fromUrl) void look(fromUrl) }, [fromUrl, look])

  const decide = async (approved: boolean) => {
    if (approved && request?.organization_id !== active?.id) {
      setError('The workspace changed. Review the request again before approving.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.decideDeviceCode(code, approved, request?.organization_id ?? '')
      setDone(approved ? 'approved' : 'denied')
    } catch {
      setError('That code is not valid any more. Ask the application for a new one.')
    }
    setBusy(false)
  }

  if (done) {
    return (
      <div className="consent">
        <h1>{done === 'approved' ? 'Device approved' : 'Device refused'}</h1>
        <p className="muted">
          {done === 'approved'
            ? 'You can close this page. The application will continue on its own.'
            : 'Nothing was granted. You can close this page.'}
        </p>
      </div>
    )
  }

  if (!request) {
    return (
      <div className="consent">
        <h1>Connect a device</h1>
        <p className="muted">Enter the code shown by the application.</p>
        <form
          className="row"
          onSubmit={(e) => { e.preventDefault(); void look(code) }}
        >
          <label className="sr-only" htmlFor="user-code">Device code</label>
          <input
            id="user-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="BCDF-GHJK"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="submit" className="btn btn-primary" disabled={busy || !code.trim()}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
        {error ? <p className="error">{error}</p> : null}
      </div>
    )
  }

  return (
    <div className="consent">
      <h1>{request.client_name} wants access to your workspace</h1>
      <p>Workspace: <strong>{active?.name ?? 'No workspace selected'}</strong></p>
      <p className="muted small">This covers the selected workspace, rather than one linked project. Your current role also limits access.</p>
      {request.organization_id !== active?.id && <p className="notice">The workspace changed. <button className="btn" onClick={() => void look(code)} disabled={busy}>Review again</button></p>}
      <p className="muted">
        Approve this only if you just started it yourself, on a device you are using now.
      </p>

      <ul className="consent-scopes">
        {request.scopes.map((s) => (
          <li key={s}>{oauthScopeWords(s)}</li>
        ))}
      </ul>

      {error ? <p className="error">{error}</p> : null}

      <div className="row">
        <button type="button" className="btn btn-primary" onClick={() => void decide(true)} disabled={busy || request.organization_id !== active?.id}>
          {busy ? 'Working…' : 'Approve'}
        </button>
        <button type="button" className="btn" onClick={() => void decide(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
